import { t } from "@/i18n/id";

/** Wording of the wizard's info step and summary: what "name" and "audience" mean depends on the site. */
export interface InfoCopy {
  title: string;
  subtitle: string;
  note: string;
  aiNote: string;
  name: string;
  namePlaceholder: string;
  nameHint?: string;
  whatsappHint: string;
  city: string;
  cityPlaceholder: string;
  description: string;
  descriptionPlaceholder: string;
  audience: string;
  audiencePlaceholder: string;
  /** Fallback when the template has no `brief.offeringsLabel`. */
  offerings: string;
  summaryKind: string;
  summaryName: string;
  /** Autofill hint for the name field: people's names must not suggest an organization. */
  autoComplete: string;
}

const business: InfoCopy = {
  title: t.wizard.info.title,
  subtitle: t.wizard.info.subtitle,
  note: t.wizard.info.note,
  aiNote: t.wizard.info.aiNote,
  name: t.wizard.info.name,
  namePlaceholder: t.wizard.info.namePlaceholder,
  whatsappHint: t.wizard.info.whatsappHint,
  city: t.wizard.info.city,
  cityPlaceholder: t.wizard.info.cityPlaceholder,
  description: t.wizard.info.description,
  descriptionPlaceholder: t.wizard.info.descriptionPlaceholder,
  audience: t.wizard.info.audience,
  audiencePlaceholder: t.wizard.info.audiencePlaceholder,
  offerings: t.wizard.info.offerings(""),
  summaryKind: t.wizard.create.summaryIndustry,
  summaryName: t.wizard.create.summaryName,
  autoComplete: "organization",
};

const event: Partial<InfoCopy> = {
  title: "Ceritakan sedikit tentang acara Anda",
  note: "Kami siapkan contoh isi sesuai jenis acara Anda, dan Anda bebas mengubahnya kapan saja. Kendali 100% di tangan Anda.",
  aiNote: "AI bantu buatkan isi undangan, Anda bebas mengubahnya kapan saja. Kendali 100% di tangan Anda.",
  whatsappHint: "Tamu menghubungi Anda lewat nomor ini.",
  name: "Nama acara",
  namePlaceholder: "mis. Festival Kopi Nusantara",
  city: "Kota acara",
  description: "Tentang acara",
  descriptionPlaceholder: "Acara apa ini dan apa yang menarik dari acaranya?",
  audience: "Siapa yang diundang",
  audiencePlaceholder: "mis. keluarga, rekan kerja",
  offerings: "Rangkaian acara",
  summaryKind: "Jenis acara",
  autoComplete: "off",
};

const BY_INDUSTRY: Record<string, Partial<InfoCopy>> = {
  "wedding-invitation": {
    ...event,
    title: "Ceritakan sedikit tentang pernikahan Anda",
    name: "Nama mempelai",
    namePlaceholder: "mis. Alya & Dimas",
    nameHint: "Ditulis seperti di undangan. Dipakai sebagai judul undangan.",
    city: "Kota pernikahan",
    description: "Kata pembuka",
    descriptionPlaceholder: "Kisah singkat atau kata sambutan untuk para tamu",
    offerings: "Rangkaian acara",
    summaryKind: "Jenis undangan",
    summaryName: "Mempelai",
  },
  birthday: {
    ...event,
    title: "Ceritakan sedikit tentang pesta Anda",
    name: "Nama yang berulang tahun",
    namePlaceholder: "mis. Ayla",
    city: "Kota pesta",
    description: "Kata pembuka",
    descriptionPlaceholder: "Ajakan singkat untuk para tamu",
    summaryKind: "Jenis undangan",
    summaryName: "Yang berulang tahun",
  },
  "event-organizer": { ...event },
  mosque: {
    title: "Ceritakan sedikit tentang komunitas Anda",
    name: "Nama masjid / komunitas",
    namePlaceholder: "mis. Masjid Al-Falah",
    description: "Tentang kami",
    descriptionPlaceholder: "Sejak kapan berdiri dan kegiatan apa saja yang dijalankan?",
    audience: "Jamaah / anggota",
    audiencePlaceholder: "mis. warga sekitar, remaja",
    offerings: "Kegiatan & program",
    summaryKind: "Jenis lembaga",
  },
  organization: {
    title: "Ceritakan sedikit tentang organisasi Anda",
    name: "Nama organisasi",
    namePlaceholder: "mis. Yayasan Peduli Sesama",
    description: "Tentang organisasi",
    descriptionPlaceholder: "Apa tujuan dan kegiatan utamanya?",
    audience: "Siapa yang dilayani",
    audiencePlaceholder: "mis. donatur, relawan, anggota",
    offerings: "Program",
    summaryKind: "Jenis organisasi",
  },
};

const PERSONAL = new Set(["personal-portfolio", "tutor", "musician", "photographer", "health-practice", "creator", "property-agent"]);

export function infoCopy(industry: string | undefined): InfoCopy {
  const own = industry ? BY_INDUSTRY[industry] : undefined;
  if (own) return { ...business, ...own };
  if (industry && PERSONAL.has(industry)) {
    return {
      ...business,
      title: "Ceritakan sedikit tentang diri Anda",
      name: "Nama Anda / nama brand",
      namePlaceholder: "mis. Rani Putri",
      description: "Tentang Anda",
      descriptionPlaceholder: "Siapa Anda dan apa yang Anda tawarkan?",
      autoComplete: "name",
    };
  }
  return business;
}
